/**
 * Single source of truth for furniture placement modes.
 *
 * `placement_mode` arrives from the API / DB / PIXI sprite data as a raw string.
 * Historically it was compared inline — `(x.placement_mode ?? 'floor') === 'surface'` —
 * in ~15 scattered ternaries across the interior scene. Nothing declared, in one place,
 * what each mode is allowed to do, so adding a mode meant a find-and-fix hunt.
 *
 * This module owns three things:
 *   1. the closed set of modes            → `PlacementMode`
 *   2. the single string→mode boundary    → `toPlacementMode`
 *   3. the semantic questions the scene actually asks about a mode → the predicates
 *
 * Adding a mode = extend `PlacementMode`; the compiler then flags every predicate
 * whose behaviour must be decided (the exhaustive `switch`es), instead of leaving
 * 15 silent `=== 'surface'` sites to be found by hand.
 *
 * No React, no PIXI, no Zustand — pure domain (ESLint enforces this).
 */

export type PlacementMode = 'floor' | 'surface';

/** Legacy rows and fresh items with no explicit mode are floor-placed. */
export const DEFAULT_PLACEMENT: PlacementMode = 'floor';

/**
 * Normalise a raw `placement_mode` string into a typed mode. THE single place a
 * string becomes a `PlacementMode`.
 *
 * Behaviour is preserved verbatim from the old inline
 * `(raw ?? 'floor') === 'surface' ? 'surface' : 'floor'`: anything that is not
 * exactly `'surface'` — `null`, `undefined`, `''`, an unknown string — collapses to
 * the default (`'floor'`). Every old call site compared against `'surface'` and
 * treated everything else as floor, so this collapse changes no observable result.
 */
export function toPlacementMode(raw: string | null | undefined): PlacementMode {
  return raw === 'surface' ? 'surface' : DEFAULT_PLACEMENT;
}

/**
 * Is this item placed ON a surface (a nightstand top, a table top …)?
 * A surface item takes its z-index from the surface it rests on and is carried with
 * that surface when dragged. A floor item is never a surface item.
 *
 * This is the literal identity test, not a capability decision: a hypothetical new
 * mode is simply "not a surface", so no per-mode choice is needed here.
 */
export function isSurface(mode: PlacementMode): boolean {
  return mode === 'surface';
}

/**
 * Does this item occupy floor footprint tiles — and therefore block walking and
 * conflict with other floor items during placement? Floor items do; surface items
 * rest on top of a surface and claim no floor tile.
 *
 * Written as an exhaustive `switch` on purpose: a new mode (e.g. `'wall'`) MUST decide
 * here whether it participates in the floor footprint. The `assertNever` default turns
 * a forgotten decision into a compile error, not a silent wrong answer at runtime.
 */
export function participatesInFootprint(mode: PlacementMode): boolean {
  switch (mode) {
    case 'floor':
      return true;
    case 'surface':
      return false;
    default:
      return assertNever(mode);
  }
}

function assertNever(mode: never): never {
  throw new Error(`Unhandled placement mode: ${String(mode)}`);
}
